import { useEffect, useMemo, useState } from "react";
import { STATUSES, PRIORITY_ORDER } from "../lib/api";

const STATUS_ORDER = STATUSES.reduce((acc, s, i) => ({ ...acc, [s.key]: i }), {});

export const SORTS = [
  { key: "recent", label: "Most recent" },
  { key: "company", label: "Company name" },
  { key: "status", label: "Status" },
  { key: "location", label: "Location" },
  { key: "salary", label: "Salary" },
  { key: "priority", label: "Priority" },
];

const salaryValue = (j) => {
  const { salary_min: mn, salary_max: mx } = j;
  if (mn != null && mx != null) return (mn + mx) / 2;
  if (mn != null) return mn;
  if (mx != null) return mx;
  return null;
};

// Owns the Jobs page filter/sort state and derives the filtered+sorted list.
// Behavior-preserving extraction from Jobs.jsx.
export function useJobFilters(jobs, showRejectedInMain) {
  const [statusSel, setStatusSel] = useState([]); // empty = all
  const [q, setQ] = useState("");
  const [locQ, setLocQ] = useState("");
  const [salMin, setSalMin] = useState("");
  const [salMax, setSalMax] = useState("");
  const [prioFilter, setPrioFilter] = useState("all");
  const [sortBy, setSortBy] = useState(() => {
    const saved = localStorage.getItem("reinvocation.jobSort");
    return SORTS.some((s) => s.key === saved) ? saved : "recent";
  });

  useEffect(() => {
    localStorage.setItem("reinvocation.jobSort", sortBy);
  }, [sortBy]);

  const toggleStatus = (key) =>
    setStatusSel((sel) => (sel.includes(key) ? sel.filter((s) => s !== key) : [...sel, key]));

  const clearFilters = () => {
    setStatusSel([]); setQ(""); setLocQ(""); setSalMin(""); setSalMax(""); setPrioFilter("all");
  };
  const hasFilters = statusSel.length > 0 || q || locQ || salMin || salMax || prioFilter !== "all";

  const filtered = useMemo(() => {
    const ql = q.toLowerCase().trim();
    const locl = locQ.toLowerCase().trim();
    const fMin = salMin === "" ? null : Number(salMin);
    const fMax = salMax === "" ? null : Number(salMax);
    const salaryMatch = (j) => {
      if (fMin === null && fMax === null) return true;
      const jMin = j.salary_min ?? j.salary_max;
      const jMax = j.salary_max ?? j.salary_min;
      if (jMin == null && jMax == null) return false;
      if (fMin !== null && jMax != null && jMax < fMin) return false;
      if (fMax !== null && jMin != null && jMin > fMax) return false;
      return true;
    };
    return jobs.filter((j) =>
      (statusSel.length === 0 || statusSel.includes(j.status)) &&
      (showRejectedInMain || statusSel.includes("rejected") || j.status !== "rejected") &&
      (prioFilter === "all" || (j.priority || "medium") === prioFilter) &&
      (!ql || j.title.toLowerCase().includes(ql) || j.company.toLowerCase().includes(ql)) &&
      (!locl || (j.remote && "remote".includes(locl)) || (j.location || "").toLowerCase().includes(locl)) &&
      salaryMatch(j)
    );
  }, [jobs, statusSel, q, locQ, salMin, salMax, prioFilter, showRejectedInMain]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const loc = (j) => (j.remote ? "remote" : (j.location || "")).toLowerCase();
    switch (sortBy) {
      case "company":
        arr.sort((a, b) => (a.company || "").localeCompare(b.company || ""));
        break;
      case "status":
        arr.sort((a, b) => (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99));
        break;
      case "location":
        arr.sort((a, b) => {
          const la = loc(a), lb = loc(b);
          if (!la && lb) return 1;
          if (la && !lb) return -1;
          return la.localeCompare(lb);
        });
        break;
      case "salary":
        arr.sort((a, b) => {
          const sa = salaryValue(a), sb = salaryValue(b);
          if (sa == null && sb == null) return 0;
          if (sa == null) return 1;
          if (sb == null) return -1;
          return sb - sa;
        });
        break;
      case "priority":
        arr.sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 2) - (PRIORITY_ORDER[b.priority] ?? 2));
        break;
      default: // recent
        arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return arr;
  }, [filtered, sortBy]);

  return {
    statusSel, setStatusSel, q, setQ, locQ, setLocQ, salMin, setSalMin, salMax, setSalMax,
    prioFilter, setPrioFilter, sortBy, setSortBy,
    toggleStatus, clearFilters, hasFilters, sorted,
  };
}
